import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import api from "../../services/api";
import { getSocket } from "../../services/socket";
import Dropdown from "../../Components/Dropdown/Dropdown";
import TextField from "../../Components/TextField/TextField";
import "./Transfers.css";

/*
 * Transfers page.
 *
 *   "Pending" tab — actionable queue of PENDING_REQUEST tags. Manager/admin
 *   can accept (same-wh moves immediately; cross-wh goes READY and waits for
 *   the gate) or reject. Existing flow, unchanged.
 *
 *   "History" tab — read-only timeline of every decided transfer, rebuilt
 *   from audit events (TRANSFER_REQUESTED + ACCEPTED/REJECTED + CHECKIN).
 *   Shows who requested it, who decided it, when, same-vs-cross warehouse,
 *   and for cross-warehouse accepts whether the gate scan actually finished
 *   the move. Rows are clickable — jump to TagDetails for the full picture.
 */

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "";
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
};

const Transfers = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isManagerOrAdmin } = useAuth();

  // "pending" | "history"
  const [activeTab, setActiveTab] = useState("pending");

  // --------- Pending tab state (unchanged behaviour) ------------------------
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRoom, setFilterRoom] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  // --------- History tab state ---------------------------------------------
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [historyOutcome, setHistoryOutcome] = useState("all"); // all | accepted | rejected
  const [historyGate, setHistoryGate] = useState("all");       // all | same | cross | awaiting | completed
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 25;

  const fetchRooms = async () => {
    try {
      const data = await api.getRooms({ forTagAssignment: true });
      setRooms(data.rooms || []);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
      setRooms([]);
    }
  };

  const fetchPendingTransfers = async () => {
    if (!isManagerOrAdmin()) return;
    setLoading(true);
    try {
      const data = await api.getPendingTransfers();
      setPendingTransfers(data.tags || []);
    } catch (error) {
      showToast(error.message || "Failed to fetch pending transfers", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!isManagerOrAdmin()) return;
    setHistoryLoading(true);
    try {
      const data = await api.getTransferHistory({
        outcome: historyOutcome,
        q: historySearch,
        page: historyPage,
        limit: HISTORY_PAGE_SIZE,
      });
      setHistory(data.transfers || []);
      setHistoryTotal(data.total || 0);
    } catch (error) {
      showToast(error.message || "Failed to fetch transfer history", 3000, true, "danger");
      setHistory([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
    fetchPendingTransfers();
  }, []);

  // Pending auto-refreshes on tag socket events.
  useEffect(() => {
    const socket = getSocket();
    const onTagsChanged = () => {
      fetchPendingTransfers();
      // If the user is on the History tab a new event likely added a row —
      // refresh in the background so they see it.
      if (activeTab === "history") fetchHistory();
    };
    socket.on("tags:added", onTagsChanged);
    socket.on("tags:updated", onTagsChanged);
    socket.on("tags:deleted", onTagsChanged);
    return () => {
      socket.off("tags:added", onTagsChanged);
      socket.off("tags:updated", onTagsChanged);
      socket.off("tags:deleted", onTagsChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load history whenever the user lands on the tab or the filters change.
  useEffect(() => {
    if (activeTab === "history") fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, historyOutcome, historyPage]);

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    if (activeTab !== "history") return;
    const t = setTimeout(() => {
      setHistoryPage(1);
      fetchHistory();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historySearch]);

  // ------------------- Pending tab derived data ----------------------------

  const searchLower = (searchTerm || "").trim().toLowerCase();

  const filteredTransfers = pendingTransfers.filter((tag) => {
    if (searchLower) {
      const epc = (tag.epc || "").toLowerCase();
      const tid = String(tag.tid || "").toLowerCase();
      const name = (tag.name || "").toLowerCase();
      const matchesSearch =
        epc.includes(searchLower) || tid.includes(searchLower) || name.includes(searchLower);
      if (!matchesSearch) return false;
    }
    if (filterRoom === "all") return true;
    const rid = parseInt(filterRoom);
    const fromRid = tag.pendingFromRid ?? tag.rid ?? 0;
    const toRid = tag.pendingRid ?? 0;
    return fromRid === rid || toRid === rid;
  });

  const sortedTransfers = [...filteredTransfers].sort((a, b) => {
    if (sortBy === "name") {
      const nameA = (a.name || "Unknown").toLowerCase();
      const nameB = (b.name || "Unknown").toLowerCase();
      return nameA.localeCompare(nameB);
    }
    if (sortBy === "time") {
      const tA = new Date(a.timestamp || 0).getTime();
      const tB = new Date(b.timestamp || 0).getTime();
      return tA - tB;
    }
    return 0;
  });

  const roomOptions = [
    { label: "All partitions", value: "all" },
    ...rooms.map((r) => ({ label: `${r.roomTitle} (RID ${r.rid})`, value: String(r.rid) })),
  ];

  const getGroupKey = (tag) => {
    const t = tag?.updatedAt || tag?.createdAt || tag?.timestamp || 0;
    const ms = new Date(t || 0).getTime() || 0;
    const bucketMs = 5000;
    const bucket = Math.floor(ms / bucketMs) * bucketMs;
    if (!bucket) return `no-time-${tag?.tid || ""}`;
    return `time-${bucket}`;
  };

  const groupedTransfers = sortedTransfers.reduce((acc, tag) => {
    const key = getGroupKey(tag);
    if (!acc[key]) acc[key] = [];
    acc[key].push(tag);
    return acc;
  }, {});

  const groups = Object.values(groupedTransfers).sort((ga, gb) => {
    if (sortBy === "name") {
      const nameA = (ga[0]?.name || "Unknown").toLowerCase();
      const nameB = (gb[0]?.name || "Unknown").toLowerCase();
      return nameA.localeCompare(nameB);
    }
    if (sortBy === "time") {
      const tA = Math.min(...ga.map((t) => new Date(t.timestamp || 0).getTime()));
      const tB = Math.min(...gb.map((t) => new Date(t.timestamp || 0).getTime()));
      return tA - tB;
    }
    return 0;
  });

  const handleAcceptGroup = async (tags) => {
    try {
      await Promise.all(tags.map((t) => api.acceptTransfer(t.tid)));
      showToast(`Accepted ${tags.length} transfer${tags.length > 1 ? "s" : ""}`, 2000, true, "success");
      setPendingTransfers((prev) => prev.filter((t) => !tags.some((g) => g.tid === t.tid)));
    } catch (error) {
      showToast(error.message || "Failed to accept transfer(s)", 3000, true, "danger");
    }
  };

  const handleRejectGroup = async (tags) => {
    try {
      await Promise.all(tags.map((t) => api.rejectTransfer(t.tid)));
      showToast(`Rejected ${tags.length} transfer${tags.length > 1 ? "s" : ""}`, 2000, true, "warning");
      setPendingTransfers((prev) => prev.filter((t) => !tags.some((g) => g.tid === t.tid)));
    } catch (error) {
      showToast(error.message || "Failed to reject transfer(s)", 3000, true, "danger");
    }
  };

  const handleAcceptOne = async (tid) => {
    try {
      await api.acceptTransfer(tid);
      showToast("Accepted transfer", 2000, true, "success");
      setPendingTransfers((prev) => prev.filter((t) => t.tid !== tid));
    } catch (error) {
      showToast(error.message || "Failed to accept transfer", 3000, true, "danger");
    }
  };

  const handleRejectOne = async (tid) => {
    try {
      await api.rejectTransfer(tid);
      showToast("Rejected transfer", 2000, true, "warning");
      setPendingTransfers((prev) => prev.filter((t) => t.tid !== tid));
    } catch (error) {
      showToast(error.message || "Failed to reject transfer", 3000, true, "danger");
    }
  };

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const roomNameByRid = useMemo(() => {
    const m = new Map();
    for (const r of rooms) m.set(r.rid, r.roomTitle || `RID ${r.rid}`);
    return m;
  }, [rooms]);

  const getPartitionLabel = (rid) => {
    const num = parseInt(rid);
    const title = roomNameByRid.get(num);
    if (title) return `${title} (RID ${num})`;
    return num === 0 ? "Unassigned" : `RID ${num}`;
  };

  const renderRow = (groupTags) => {
    const tag = groupTags[0];
    const toRid = tag.pendingRid ?? 0;
    const toLabel = getPartitionLabel(toRid);
    const fromRids = [...new Set(groupTags.map((t) => t.pendingFromRid ?? t.rid ?? 0))];
    const fromLabel =
      fromRids.length === 1
        ? getPartitionLabel(fromRids[0])
        : "Various partitions";
    const rowKey = tag.pendingBatchId || `single-${tag.tid}`;
    const totalCount = groupTags.length;
    const isExpanded = expandedGroups.has(rowKey);

    const byProduct = groupTags.reduce((acc, t) => {
      const name = t.name || "Unknown";
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});
    const productSummary = Object.entries(byProduct)
      .map(([name, count]) => (count > 1 ? `${name} ×${count}` : name))
      .join(", ");

    return (
      <div key={rowKey} className="transfer-group">
        <div className="transfer-row">
          <div className="transfer-main">
            <button
              type="button"
              className="transfer-toggle"
              onClick={() => toggleGroup(rowKey)}
              aria-expanded={isExpanded}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              <i className={`fa ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"}`} />
            </button>
            <div className="transfer-product">
              <span className="transfer-product-name">{productSummary}</span>
              {totalCount > 1 && (
                <span className="transfer-count">×{totalCount} tags</span>
              )}
            </div>
            <div className="transfer-route">
              <span className="transfer-room">{fromLabel}</span>
              <i className="fa fa-arrow-right transfer-arrow" />
              <span className="transfer-room">{toLabel}</span>
            </div>
          </div>
          <div className="transfer-actions">
            <button className="transfer-btn accept" onClick={() => handleAcceptGroup(groupTags)}>
              <i className="fa fa-check" /> {totalCount > 1 ? `Accept all (${totalCount})` : "Accept"}
            </button>
            <button className="transfer-btn reject" onClick={() => handleRejectGroup(groupTags)}>
              <i className="fa fa-times" /> {totalCount > 1 ? `Reject all (${totalCount})` : "Reject"}
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="transfer-details">
            <div className="transfer-details-header">
              <span className="transfer-details-title">Tags in this transfer</span>
              <span className="transfer-details-count">{groupTags.length}</span>
            </div>
            <div className="transfer-details-list">
              {groupTags
                .slice()
                .sort((a, b) => String(a.tid || "").localeCompare(String(b.tid || "")))
                .map((t) => (
                  <div key={t.tid} className="transfer-tag-row">
                    <div className="transfer-tag-main">
                      <div className="transfer-tag-title">
                        <span className="transfer-tag-name">{t.name || "Unknown"}</span>
                        <span className="transfer-tag-tid">TID: {t.tid || "-"}</span>
                      </div>
                      <div className="transfer-tag-meta">
                        <span className="transfer-tag-epc">EPC: {t.epc || "-"}</span>
                      </div>
                    </div>
                    <div className="transfer-tag-actions">
                      <button className="transfer-btn accept transfer-btn-sm" onClick={() => handleAcceptOne(t.tid)}>
                        <i className="fa fa-check" /> Accept
                      </button>
                      <button className="transfer-btn reject transfer-btn-sm" onClick={() => handleRejectOne(t.tid)}>
                        <i className="fa fa-times" /> Reject
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ------------------- History tab derived data ---------------------------

  // Secondary client-side filter for the "Gate / scope" dropdown. The
  // backend already filters by outcome and search string; we narrow further
  // on same-vs-cross and gate completion without making another round trip.
  const filteredHistory = history.filter((row) => {
    if (historyGate === "all") return true;
    if (historyGate === "same") return row.sameWarehouse === true;
    if (historyGate === "cross") return row.sameWarehouse === false;
    if (historyGate === "awaiting") return row.gateStatus === "awaiting_gate";
    if (historyGate === "completed") return row.gateStatus === "completed";
    return true;
  });

  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));

  const openTagDetails = (tid) => {
    if (!tid) return;
    navigate(`/tags/${encodeURIComponent(tid)}`);
  };

  const renderHistoryRow = (row) => {
    const outcomeTone = row.outcome === "accepted" ? "accepted" : "rejected";
    const isCrossWh = row.sameWarehouse === false;
    const gateChip = (() => {
      if (row.outcome !== "accepted") return null;
      if (row.sameWarehouse) {
        return <span className="th-chip th-chip-same" title="Same-warehouse transfer">Same warehouse</span>;
      }
      if (row.gateStatus === "completed") {
        return (
          <span className="th-chip th-chip-completed" title={`Gate-confirmed at ${formatDateTime(row.gateCompletedAt)}`}>
            <i className="fa fa-check-circle" aria-hidden /> Gate completed
          </span>
        );
      }
      return (
        <span className="th-chip th-chip-awaiting" title="Awaiting gate scan to finalise">
          <i className="fa fa-hourglass-half" aria-hidden /> Awaiting gate
        </span>
      );
    })();

    return (
      <button
        key={row._id}
        type="button"
        className={`th-row th-row-${outcomeTone}`}
        onClick={() => openTagDetails(row.tid)}
        title="Open tag details"
      >
        <div className={`th-accent th-accent-${outcomeTone}`} />
        <div className="th-main">
          <div className="th-top">
            <div className="th-title">
              <span className="th-product">{row.productName || "Unknown"}</span>
              <span className="th-tid">TID: {row.tid || "—"}</span>
            </div>
            <div className="th-chips">
              <span className={`th-chip th-chip-${outcomeTone}`}>
                <i className={`fa ${row.outcome === "accepted" ? "fa-check" : "fa-times"}`} aria-hidden />
                {row.outcome === "accepted" ? "Accepted" : "Rejected"}
              </span>
              {isCrossWh && (
                <span className="th-chip th-chip-cross" title="Cross-warehouse transfer">
                  <i className="fa fa-exchange" aria-hidden /> Cross warehouse
                </span>
              )}
              {gateChip}
            </div>
          </div>

          <div className="th-route">
            <div className="th-route-end">
              <span className="th-route-label">From</span>
              <span className="th-route-room">{row.fromRoom?.roomTitle || "—"}</span>
              {row.fromRoom?.warehouseTitle && (
                <span className="th-route-wh">{row.fromRoom.warehouseTitle}</span>
              )}
            </div>
            <i className="fa fa-long-arrow-right th-route-arrow" aria-hidden />
            <div className="th-route-end">
              <span className="th-route-label">To</span>
              <span className="th-route-room">{row.toRoom?.roomTitle || "—"}</span>
              {row.toRoom?.warehouseTitle && (
                <span className="th-route-wh">{row.toRoom.warehouseTitle}</span>
              )}
            </div>
          </div>

          <div className="th-meta">
            {row.requestedAt && (
              <span className="th-meta-item" title={formatDateTime(row.requestedAt)}>
                <i className="fa fa-paper-plane" aria-hidden />
                Requested by <strong>{row.requestedBy || "—"}</strong> · {formatRelative(row.requestedAt)}
              </span>
            )}
            <span className="th-meta-item" title={formatDateTime(row.decidedAt)}>
              <i className={`fa ${row.outcome === "accepted" ? "fa-thumbs-up" : "fa-thumbs-down"}`} aria-hidden />
              {row.outcome === "accepted" ? "Accepted" : "Rejected"} by <strong>{row.decidedBy || "—"}</strong> · {formatRelative(row.decidedAt)}
            </span>
            {row.gateStatus === "completed" && (
              <span className="th-meta-item" title={formatDateTime(row.gateCompletedAt)}>
                <i className="fa fa-shield" aria-hidden />
                Gate completed · {formatRelative(row.gateCompletedAt)}
              </span>
            )}
            {row.epc && (
              <span className="th-meta-item th-meta-epc" title={row.epc}>
                <i className="fa fa-tag" aria-hidden /> EPC: {row.epc}
              </span>
            )}
          </div>
        </div>
        <i className="fa fa-chevron-right th-chevron" aria-hidden />
      </button>
    );
  };

  // ------------------- Render ----------------------------------------------

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Transfers</h1>
            <p className="dashboard-subtitle">
              Review pending partition transfers and browse the history of every decision.
            </p>
          </div>
        </div>

        {/* Tab switcher — Pending is actionable, History is audit-sourced. */}
        <div className="transfers-tabs">
          <button
            type="button"
            className={`transfers-tab${activeTab === "pending" ? " is-active" : ""}`}
            onClick={() => setActiveTab("pending")}
          >
            <i className="fa fa-inbox" aria-hidden />
            <span>Pending</span>
            {pendingTransfers.length > 0 && (
              <span className="transfers-tab-count">{pendingTransfers.length}</span>
            )}
          </button>
          <button
            type="button"
            className={`transfers-tab${activeTab === "history" ? " is-active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            <i className="fa fa-history" aria-hidden />
            <span>History</span>
          </button>
        </div>

        {activeTab === "pending" ? (
          <div className="transfers-card">
            <div className="transfers-card-header">
              <div className="transfers-card-header-left">
                <div className="transfers-card-header-icon">
                  <i className="fa fa-exchange" />
                </div>
                <div>
                  <h2>Pending Tag Transfers</h2>
                  <p>Review and approve or reject partition transfer requests.</p>
                </div>
              </div>
              <span className={`transfers-badge ${pendingTransfers.length === 0 ? "empty" : ""}`}>
                {pendingTransfers.length} pending
              </span>
            </div>
            {loading ? (
              <div className="transfers-loading">
                <i className="fa fa-spinner fa-spin" />
                <span>Loading transfers...</span>
              </div>
            ) : pendingTransfers.length === 0 ? (
              <div className="transfers-empty">
                <div className="transfers-empty-icon">
                  <i className="fa fa-inbox" />
                </div>
                <p>No pending transfers right now.</p>
              </div>
            ) : (
              <>
                <div className="transfers-filters">
                  <TextField
                    placeholder="Search by EPC, Tag ID, or Product name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    iconLeft="fa-search"
                    variant="outlined"
                    style={{ minWidth: 260 }}
                  />
                  <Dropdown
                    label="Filter by partition"
                    options={roomOptions}
                    value={filterRoom}
                    onChange={setFilterRoom}
                    placeholder="All partitions"
                    width="200px"
                  />
                  <Dropdown
                    label="Sort by"
                    options={[
                      { label: "Product name", value: "name" },
                      { label: "Time (oldest first)", value: "time" },
                    ]}
                    value={sortBy}
                    onChange={setSortBy}
                    placeholder="Sort by"
                    width="200px"
                  />
                </div>
                <div className="transfers-list">
                  {groups.length === 0 ? (
                    <div className="transfers-empty transfers-empty-inline">
                      <p>No transfers match the search or filters.</p>
                    </div>
                  ) : (
                    groups.map(renderRow)
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="transfers-card">
            <div className="transfers-card-header">
              <div className="transfers-card-header-left">
                <div className="transfers-card-header-icon th-header-icon">
                  <i className="fa fa-history" />
                </div>
                <div>
                  <h2>Transfer History</h2>
                  <p>Every accepted or rejected transfer, reconstructed from the audit trail.</p>
                </div>
              </div>
              <span className={`transfers-badge ${historyTotal === 0 ? "empty" : ""}`}>
                {historyTotal} record{historyTotal === 1 ? "" : "s"}
              </span>
            </div>

            <div className="transfers-filters">
              <TextField
                placeholder="Search product, tag, room, or user..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                iconLeft="fa-search"
                variant="outlined"
                style={{ minWidth: 260 }}
              />
              <Dropdown
                label="Outcome"
                options={[
                  { label: "All decisions", value: "all" },
                  { label: "Accepted only", value: "accepted" },
                  { label: "Rejected only", value: "rejected" },
                ]}
                value={historyOutcome}
                onChange={(v) => { setHistoryOutcome(v); setHistoryPage(1); }}
                placeholder="Outcome"
                width="200px"
              />
              <Dropdown
                label="Scope"
                options={[
                  { label: "All transfers", value: "all" },
                  { label: "Same warehouse", value: "same" },
                  { label: "Cross warehouse", value: "cross" },
                  { label: "Awaiting gate", value: "awaiting" },
                  { label: "Gate completed", value: "completed" },
                ]}
                value={historyGate}
                onChange={setHistoryGate}
                placeholder="Scope"
                width="200px"
              />
              <button
                type="button"
                className="th-refresh-btn"
                onClick={fetchHistory}
                disabled={historyLoading}
                title="Refresh history"
              >
                <i className={`fa ${historyLoading ? "fa-spinner fa-spin" : "fa-refresh"}`} aria-hidden />
                <span>Refresh</span>
              </button>
            </div>

            {historyLoading && history.length === 0 ? (
              <div className="transfers-loading">
                <i className="fa fa-spinner fa-spin" />
                <span>Loading history...</span>
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="transfers-empty">
                <div className="transfers-empty-icon">
                  <i className="fa fa-folder-open-o" />
                </div>
                <p>No transfers match these filters yet.</p>
              </div>
            ) : (
              <>
                <div className="th-list">
                  {filteredHistory.map(renderHistoryRow)}
                </div>

                {historyTotalPages > 1 && (
                  <div className="th-pagination">
                    <button
                      type="button"
                      className="th-page-btn"
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      disabled={historyPage <= 1 || historyLoading}
                    >
                      <i className="fa fa-chevron-left" aria-hidden /> Previous
                    </button>
                    <span className="th-page-label">
                      Page {historyPage} of {historyTotalPages}
                    </span>
                    <button
                      type="button"
                      className="th-page-btn"
                      onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                      disabled={historyPage >= historyTotalPages || historyLoading}
                    >
                      Next <i className="fa fa-chevron-right" aria-hidden />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Transfers;
